'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

/**
 * Global scroll-reveal for the marketing site – ports the prototype's single
 * IntersectionObserver over all `.reveal` elements. Mounted once in the
 * marketing layout; re-scans on route change.
 *
 * Wurzel-Fix (ersetzt die alte Klassen-Allowlist): Ein Element soll bei 12%
 * Sichtbarkeit erscheinen – aber Container, die HÖHER als der Viewport sind,
 * erreichen 12% nie und blieben permanent auf opacity:0 (unsichtbar). Statt
 * einzelne Klassen (`.article`, `.help-article-body`, …) auszunehmen, erkennt
 * der Observer das generisch: „12% sichtbar ODER höher als der Viewport".
 */
export function RevealController() {
  const pathname = usePathname();

  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.marketing-scope .reveal:not(.is-visible)',
      ),
    );
    if (nodes.length === 0) return;

    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          // Höher als der Viewport? Dann ist 12% unerreichbar → sichtbar machen,
          // sobald das Element den Viewport überhaupt berührt. Sonst die
          // gewohnte 12%-Schwelle (rootBounds kann null sein → 12%-Pfad).
          const tallerThanViewport =
            entry.rootBounds !== null &&
            entry.boundingClientRect.height > entry.rootBounds.height;

          if (entry.intersectionRatio >= 0.12 || tallerThanViewport) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      // 0 = „berührt den Viewport" (für hohe Container), 0.12 = die normale
      // Schwelle. Beide nötig, damit der Callback in beiden Fällen feuert.
      { threshold: [0, 0.12] },
    );

    nodes.forEach((node) => io.observe(node));

    return () => io.disconnect();
  }, [pathname]);

  return null;
}
